import React, { useEffect, useState } from "react";
import axios, { CancelToken } from "axios";
import { useAppDispatch } from "hooks/redux";
import classNames from "classnames";

import { GetInvoice } from "api/rpc/2024-04/facilityAdmin/order/invoice";
import { StatusCode } from "api/protocols";

import { showError } from "redux/actions/ui";

import Sheet from "components/sheet/Sheet";
import Spin from "components/spin/spin";
import Checkbox from "components/form/checkbox/Checkbox";
import Input from "components/form/input";

import { IInvoice } from "redux/reducers/models/order";

interface IProps {
  /**Boolean trigger for loading the customer's credit books */
  loadInvoices: boolean;
  customerId: number;
  onCancel: () => void;
  onOk: (selectedBooking: Record<string, any>) => void;
  /**User permission to allow the user to search */
  searchPermissions?: boolean;
}

interface IState {
  selectedInvoice: IInvoice;
  invoices: IInvoice[];
  customerInvoices: IInvoice[];
  selectedTabIndex: number;
  modalVisible: boolean;
  invoiceSearch: string;
}

export default function InvoiceModal(props: IProps) {
  const dispatch = useAppDispatch();

  const [state, setState] = useState<IState>({
    selectedInvoice: undefined,
    invoices: undefined,
    customerInvoices: undefined,
    selectedTabIndex: 0,
    modalVisible: false,
    invoiceSearch: "",
  });

  useEffect(() => {
    const source = axios.CancelToken.source();
    void loadInvoices(source.token);
    return () => source.cancel();
  }, []);

  async function loadInvoices(token?: CancelToken) {
    const invoiceRes = await GetInvoice({ financial_status: "draft", extended: true }, token ? false : true, token);

    if (invoiceRes.status !== StatusCode.OK) {
      dispatch(showError("Error laoding invoices"));
      return;
    }

    setState(prevState => ({ ...prevState, invoices: invoiceRes.data }));
  }

  function selectInvoice(invoice: IInvoice) {
    setState(prevState => ({ ...prevState, selectedInvoice: invoice }));
  }

  function invoiceEventName(invoice: IInvoice) {
    return invoice.tournament?.name || invoice.hospitality_event?.name || "";
  }

  function invoiceMatchesSearch(invoice: IInvoice, search: string) {
    if (!search) {
      return true;
    }

    const query = search.toLocaleLowerCase();
    return [invoice.name, invoice.customer?.full_name, invoiceEventName(invoice)]
      .filter(Boolean)
      .some(value => value.toLocaleLowerCase().includes(query));
  }

  return (
    <Sheet
      open={props.loadInvoices}
      title="Select Invoice"
      onCancel={props.onCancel}
      onOk={() => props.onOk(state.selectedInvoice)}
      size="small"
      closable
      okDisabled={!state.selectedInvoice}
      okText="Process"
    >
      {state.invoices ? (
        <div>
          <Input
            labelHidden
            placeholder={"Search..."}
            onChange={e => setState(prevState => ({ ...prevState, invoiceSearch: e.target.value }))}
          />
          <div className="ui-checkbox-group mt-2" style={{ minHeight: "122px" }}>
            {state.invoices
              .filter(invoice => invoiceMatchesSearch(invoice, state.invoiceSearch))
              .map((invoice, i) => {
                const eventName = invoiceEventName(invoice);
                return (
                  <div
                    key={i}
                    onClick={() => selectInvoice(invoice)}
                    className={classNames("ui-checkbox-group-item", {
                      "ui-checkbox-group-item_selected": state.selectedInvoice?.id === invoice.id,
                    })}
                  >
                    <div className="ui-checkbox-group-item-content">
                      <div className="ui-checkbox-group-item-text">
                        <p className="text-semibold">{invoice.name}</p>
                        {invoice.customer?.full_name ? <p className="text-sm text-subdued">{invoice.customer.full_name}</p> : null}
                        {eventName ? <p className="text-sm text-subdued">{eventName}</p> : null}
                      </div>
                    </div>
                    <Checkbox
                      size="medium"
                      value={invoice.id}
                      checked={state.selectedInvoice?.id === invoice.id}
                      onChange={() => selectInvoice(invoice)}
                    />
                  </div>
                );
              })}
          </div>
        </div>
      ) : (
        <div style={{ height: "32px" }}>
          <Spin />
        </div>
      )}
    </Sheet>
  );
}
